import React, { useState } from "react";
import styled from "styled-components";
import { DatePicker, InputMoney, InputTextarea, ModalForm, Notice, Text, unMask } from "@ficatranquilo/contabilidade.com-ui";
import { getValueInputDeadlineLabel, isValueInputDeadlinePassed } from "../../helpers";
import { rangeIntersectsContractAndProjectPeriod, firstDayOfMonth, lastDayOfMonth } from "../../helpers/contractPeriod";
import { escapeHtml } from "../ow";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const monthStart = (month, year) => new Date(year, month - 1, 1);

/**
 * Modal reutilizável para Input de Valor Variável. Só monta com `show`.
 * Props:
 * - show: boolean
 * - onHide: function
 * - contract: object (dados do contrato)
 * - valueInputService: service instance
 * - onSuccess: function callback após submit
 * - preselectedMonth: number (opcional)
 * - preselectedYear: number (opcional)
 */
export default function ValueInputModal({ show, ...props }) {
  if (!show || !props.contract) return null;
  return <ValueInputForm {...props} />;
}

function ValueInputForm({ onHide, contract, valueInputService, onSuccess, preselectedMonth, preselectedYear }) {
  const today = new Date();
  const [competence, setCompetence] = useState(() =>
    monthStart(preselectedMonth || today.getMonth() + 1, preselectedYear || today.getFullYear()),
  );
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");

  const referenceMonth = competence ? competence.getMonth() + 1 : null;
  const referenceYear = competence ? competence.getFullYear() : null;

  const isBeforeDeadline = !isValueInputDeadlinePassed(
    contract?.value_input_deadline,
    contract?.value_input_deadline_month,
    referenceMonth,
    referenceYear,
  );

  function close() {
    if (loadingSubmit) return;
    onHide();
  }

  async function handleSubmit() {
    setError("");

    const numericValue = value ? Number(unMask(value, true)) : NaN;
    const errors = {};
    if (!competence) errors.competence = "Informe a competência";
    if (!value) errors.value = "Informe o valor";
    else if (isNaN(numericValue) || numericValue <= 0) errors.value = "Informe um valor válido";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    const periodCheck = rangeIntersectsContractAndProjectPeriod(
      contract,
      firstDayOfMonth(referenceMonth, referenceYear),
      lastDayOfMonth(referenceMonth, referenceYear),
    );
    if (!periodCheck.valid) {
      setError(periodCheck.reason);
      return;
    }

    setLoadingSubmit(true);
    try {
      const result = await valueInputService.submitValue(contract.id, referenceMonth, referenceYear, numericValue, notes || null);

      if (result && !result.error) {
        setLoadingSubmit(false);
        onSuccess && onSuccess(result);
        onHide();
        return;
      }
      setError(result?.msg || "Erro ao submeter valor");
    } catch (err) {
      console.error(err);
      setError("Erro ao processar a submissão do valor");
    }
    setLoadingSubmit(false);
  }

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={loadingSubmit}
      $isLoading={loadingSubmit}
      $size="medium"
      $title="Informar valor do mês"
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: loadingSubmit },
        {
          text: contract.requires_value_approval ? "Submeter para aprovação" : "Confirmar valor",
          action: handleSubmit,
          variant: "primary",
          isDisabled: loadingSubmit,
        },
      ]}
    >
      <Body>
        <Text
          $text="Prestador: <strong>%s</strong>"
          $replaces={[escapeHtml(contract?.contractor?.name || "—")]}
          $variant="paragraph_small_medium"
          $color="N700"
          $colorEmphasis="N900"
        />

        {error ? <Notice $variant="red" $text="%s" $replaces={[escapeHtml(error)]} /> : null}

        {contract.value_input_deadline ? (
          <Notice
            $variant={isBeforeDeadline ? "blue" : "red"}
            $title={isBeforeDeadline ? undefined : "O prazo para este período já passou"}
            $text="Prazo para informar: %s."
            $replaces={[getValueInputDeadlineLabel(contract.value_input_deadline, contract.value_input_deadline_month)]}
          />
        ) : null}

        {contract.requires_value_approval ? (
          <Notice
            $variant="orange"
            $title="Este contrato exige duplo check"
            $text="O valor precisará ser aprovado por outro usuário depois de enviado."
          />
        ) : null}

        <DatePicker
          $idForm="value-input-competence"
          $centralize
          $label="Competência"
          $variant="default"
          $isCompetence
          $isRequired
          $errors={fieldErrors.competence}
          $date={competence}
          $setDate={(date) => {
            setCompetence(date);
            if (fieldErrors.competence) setFieldErrors((prev) => ({ ...prev, competence: "" }));
          }}
        />
        <Field>
          <InputMoney
            $idForm="value-input-value"
            $label="Valor"
            $variant="default"
            $isRequired
            $errors={fieldErrors.value}
            placeholder="R$ 0,00"
            inputMode="numeric"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (fieldErrors.value) setFieldErrors((prev) => ({ ...prev, value: "" }));
            }}
          />
          <Text $text="Informe o valor a ser pago ao prestador neste período." $variant="caption_medium" $color="N400" />
        </Field>
        <InputTextarea
          $idForm="value-input-notes"
          $label="Observações"
          $variant="default"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Informações adicionais sobre este pagamento…"
        />
      </Body>
    </ModalForm>
  );
}
